import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    CssBaseline,
    Toolbar,
    AppBar as MuiAppBar,
    IconButton,
    useTheme,
    useMediaQuery
} from '@mui/material';
import {
    Menu as MenuIcon,
    ChevronLeft as ChevronLeftIcon,
    Login as LoginIcon,
    Dashboard as DashboardIcon,
    Business as BusinessIcon,
    Store as StoreIcon,
    Factory as FactoryIcon,
    People as PeopleIcon,
    Description as DescriptionIcon,
    AssignmentInd as AssignmentIndIcon,
    Assessment
} from '@mui/icons-material';
import RealEstateAgentIcon from '@mui/icons-material/RealEstateAgent';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import ApartmentIcon from '@mui/icons-material/Apartment';
import { styled, ThemeProvider } from '@mui/material/styles';
import { createTheme } from '@mui/material/styles';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { getRole, logout } from "../services/auth";

const theme = createTheme({
    palette: {
        primary: {
            main: '#10641c',
            contrastText: '#ffffff',
        },
        secondary: {
            main: '#e6d700',
            contrastText: '#000000',
        },
    },
    typography: {
        fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    },
});

const drawerWidth = 240;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' && prop !== 'isMobile' })(
    ({ theme, open, isMobile }) => ({
        flexGrow: 1,
        padding: theme.spacing(1),
        paddingTop: isMobile ? theme.spacing(0) : 25,
        marginLeft: isMobile ? 0 : drawerWidth,
        width: isMobile ? '100%' : `calc(100% - ${drawerWidth}px)`,
        overflowY: 'auto',
        height: '100vh',
        boxSizing: 'border-box',
    })
);

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
    justifyContent: 'space-between',
    bgcolor: '#10641c',
    color: 'white',
}));

const StyledAppBar = styled(MuiAppBar)(({ theme, isMobile }) => ({
    background: 'linear-gradient(45deg, #10641c 30%, #388e3c 90%)',
    color: '#ffffff',
    zIndex: theme.zIndex.drawer + 1,
    ...(isMobile
        ? {
            width: '100%',
        }
        : {
            width: drawerWidth,
            position: 'fixed',
            top: 0,
            left: 0,
        }),
}));

const Layout = ({ children, setAuthenticated }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [open, setOpen] = useState(false);
    const [menuItems, setMenuItems] = useState([]);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const role = getRole();
        if (role) {
            const items = getMenuItemsByUserType(role);
            setMenuItems(items);
        }
    }, []);

    const handleLogout = () => {
        logout(setAuthenticated);
    };

    const handleDrawerToggle = () => {
        setOpen(!open);
    };

    const handleDrawerClose = () => {
        setOpen(false);
    };

    const getMenuItemsByUserType = (type) => {
        const baseItems = [
            { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', group: 'Geral' },
        ];

        const typeSpecificItems = {
            'admin': [
                { text: 'Formulários', icon: <DescriptionIcon />, path: '/admin/forms-templates', group: 'Pesquisa' },
                { text: 'Empreendedores', icon: <AssignmentIndIcon />, path: '/admin/entrepreneur', group: 'Gerenciamento' },
                { text: 'Tipos de Empreendimentos', icon: <RealEstateAgentIcon />, path: '/admin/business-size', group: 'Gerenciamento' },
                { text: 'Setores', icon: <BusinessIcon />, path: '/admin/sector', group: 'Gerenciamento' },
                { text: 'Atividades econômicas', icon: <BusinessCenterIcon />, path: '/admin/type-business', group: 'Gerenciamento' },
                { text: 'Bairros e Distritos', icon: <ApartmentIcon />, path: '/admin/neighborhood', group: 'Gerenciamento' },
                { text: 'Usuários', icon: <PeopleIcon />, path: '/admin/users', group: 'Gerenciamento' },
            ],
            'team': [
                { text: 'Empreeendedores', icon: <BusinessIcon />, path: '/team/entrepreneur', group: 'Gerenciamento' },
            ],
            'entrepreneur': [
                { text: 'Meu Comércio', icon: <StoreIcon />, path: '/my-store', group: 'Comércio' },
                { text: 'Produtos', icon: <StoreIcon />, path: '/products', group: 'Comércio' },
            ],
            'city_hall': [
                { text: 'Gráficos', icon: <Assessment />, path: '/city_hall/graphic-screen', group: 'Relatórios' },
                { text: 'Formulários', icon: <DescriptionIcon />, path: '/city_hall/forms-templates', group: 'Pesquisa' },
                { text: 'Empreendedores', icon: <AssignmentIndIcon />, path: '/city_hall/entrepreneur', group: 'Gerenciamento' },
                { text: 'Tipos de Empreendimentos', icon: <RealEstateAgentIcon />, path: '/city_hall/business-size', group: 'Gerenciamento' },
                { text: 'Setores', icon: <BusinessIcon />, path: '/city_hall/sector', group: 'Gerenciamento' },
                { text: 'Atividades econômicas', icon: <BusinessCenterIcon />, path: '/city_hall/type-business', group: 'Gerenciamento' },
                { text: 'Bairros e Distritos', icon: <ApartmentIcon />, path: '/city_hall/neighborhood', group: 'Gerenciamento' },
            ],
        };
        return [...baseItems, ...(typeSpecificItems[type] || [])];
    };

    const groupedMenuItems = menuItems.reduce((acc, item) => {
        const group = item.group || 'Outros';
        acc[group] = acc[group] || [];
        acc[group].push(item);
        return acc;
    }, {});

    return (
        <ThemeProvider theme={theme}>
            <Box
                sx={{
                    display: 'flex',
                    minHeight: '100vh',
                    bgcolor: '#f8f9fa',
                }}
            >
                <CssBaseline />

                {/* AppBar */}
                <StyledAppBar position="fixed" isMobile={isMobile}>
                    <Toolbar>
                        {isMobile && (
                            <IconButton
                                color="inherit"
                                aria-label="open drawer"
                                onClick={handleDrawerToggle}
                                edge="start"
                                sx={{ mr: 0.6 }}
                            >
                                <MenuIcon />
                            </IconButton>
                        )}
                        <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 'bold' }}>
                            Conecta Mais
                        </Typography>
                    </Toolbar>
                </StyledAppBar>

                {/* Sidebar */}
                <Box
                    component="nav"
                    sx={{
                        width: isMobile ? (open ? drawerWidth : 0) : drawerWidth,
                        flexShrink: 0,
                        position: isMobile ? 'fixed' : 'fixed',
                        height: '100vh',
                        zIndex: theme.zIndex.drawer,
                        bgcolor: '#f0f0f0',
                        transition: isMobile ? 'width 0.3s ease-in-out' : 'none',
                        '& .MuiDrawer-paper': {
                            width: drawerWidth,
                            boxSizing: 'border-box',
                            bgcolor: 'white',
                            boxShadow: '2px 0 10px rgba(0, 0, 0, 0.05)',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            overflowX: 'hidden',
                        },
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    <DrawerHeader>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <Typography
                                variant="h6"
                                noWrap
                                component="div"
                                sx={{ fontWeight: 'bold', display: isMobile ? 'none' : 'block' }}
                            >
                                Conecta Mais
                            </Typography>
                            {isMobile && (
                                <IconButton onClick={handleDrawerClose} color="inherit">
                                    <ChevronLeftIcon />
                                </IconButton>
                            )}
                        </Box>
                    </DrawerHeader>
                    <Divider />
                    <List
                        sx={{
                            flexGrow: 1,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            height: 'calc(100% - 64px - 80px)',
                        }}
                    >
                        {Object.entries(groupedMenuItems).map(([group, items], index) => (
                            <React.Fragment key={group}>
                                {index > 0 && <Divider sx={{ my: 0.2 }} />}
                                {group !== 'Outros' && (
                                    <ListItem disablePadding sx={{ px: 2, py: 0.5 }}>
                                        <ListItemText
                                            primary={
                                                <Typography
                                                    variant="subtitle1"
                                                    sx={{ fontWeight: 'bold', color: 'rgba(0, 0, 0, 0.6)', fontSize: '0.9rem' }}
                                                >
                                                    {group}
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                )}
                                {items.map((item) => (
                                    <ListItem
                                        button
                                        key={item.text}
                                        component={Link}
                                        to={item.path}
                                        selected={location.pathname === item.path}
                                        onClick={handleDrawerClose}
                                        sx={{
                                            '&:hover': {
                                                bgcolor: 'rgba(16, 100, 28, 0.1)',
                                                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.08)',
                                                transform: 'scale(1.01)',
                                            },
                                            '&.Mui-selected': {
                                                bgcolor: '#10641c',
                                                color: '#ffffff',
                                                '& .MuiListItemIcon-root': {
                                                    color: '#ffffff',
                                                },
                                            },
                                            borderRadius: '0 20px 20px 0',
                                            my: 0.3,
                                            mx: 0.5,
                                            color: '#000000',
                                            paddingTop: 0.9,
                                            paddingBottom: 1,
                                        }}
                                    >
                                        <ListItemIcon sx={{ color: '#10641c', minWidth: '36px', mr: 1 }}>
                                            {item.icon}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={item.text}
                                            primaryTypographyProps={{
                                                sx: {
                                                    fontSize: '0.9rem',
                                                    lineHeight: 1.3,
                                                },
                                            }}
                                        />
                                    </ListItem>
                                ))}
                            </React.Fragment>
                        ))}
                    </List>

                    <Divider sx={{ my: 1 }} />

                    {(isMobile ? open : true) && (
                        <Box sx={{ p: 2, mt: 'auto' }}>
                            <Button
                                variant="contained"
                                startIcon={<LoginIcon />}
                                fullWidth
                                href="/login"
                                sx={{
                                    bgcolor: 'white',
                                    color: '#10641c',
                                    borderRadius: '50px',
                                    fontWeight: 'bold',
                                    textTransform: 'none',
                                    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
                                    '&:hover': {
                                        bgcolor: '#f0f8f0',
                                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                                    },
                                    transition: 'all 0.3s ease',
                                    fontSize: '0.9rem',
                                    padding: '10px 20px',
                                }}
                                onClick={handleLogout}
                            >
                                Sair
                            </Button>
                        </Box>
                    )}
                </Box>

                {/* Main content */}
                <Main open={open} isMobile={isMobile}>
                    {isMobile && <Toolbar />}
                    {children || <Outlet />}
                </Main>
            </Box>
        </ThemeProvider>
    );
};

export default Layout;